import { CaretDownIcon, CheckIcon } from '@phosphor-icons/react'
import clsx from 'clsx'
import { EmblaCarouselType } from 'embla-carousel'
import useEmblaCarousel from 'embla-carousel-react'
import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'
import { FahrlehrerCh } from '@/src/@types/blinkadmin'
import Button from '@/src/components/core/Button'
import Container from '@/src/components/layout/Container'

const Prices = ({
  selectedLocation,
}: FahrlehrerCh.TeacherProfile & {
  selectedLocation: FahrlehrerCh.TeacherProfileLocation
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [emblaRef, emblaApi] = useEmblaCarousel({
    breakpoints: {
      '(min-width: 768px)': { active: false },
    },
  })

  const [showMorePrices, setShowMorePrices] = useState(false)

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setSelectedIndex(emblaApi.selectedScrollSnap())
  }, [])

  useEffect(() => {
    if (!emblaApi) return

    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect).on('select', onSelect)
  }, [emblaApi, onSelect])

  const vkuCourse = selectedLocation.prices.courses?.find(
    (course) => course.type === 'VKU'
  )

  const examPriceRange =
    selectedLocation.prices.exams && selectedLocation.prices.exams.length > 0
      ? selectedLocation.prices.exams?.reduce(
          (range, exam) => {
            if (exam.withPreparation / 100 < range.min) {
              range.min = exam.withPreparation / 100
            }
            if (exam.withPreparation / 100 > range.max) {
              range.max = exam.withPreparation / 100
            }
            return range
          },
          { min: Infinity, max: -Infinity }
        )
      : null

  return (
    <section id="prices" className="mt-20 md:mt-24">
      <Container fullPageOnMobile>
        <h2 className="mx-5 text-4xl font-black md:mx-0 md:text-10xl">
          Preise
        </h2>
        <p className="mx-5 mt-4 text-gray-800 md:text-lg lg:mx-0">
          Nach einer Probelektion entscheidest du in Ruhe, wie es weitergeht.
          Hier findest du eine transparente Übersicht aller Kosten.
        </p>
        <div ref={emblaRef} className="mt-6 overflow-hidden">
          <div className="flex select-none md:grid md:grid-cols-3 md:gap-x-5">
            <div className="ml-5 min-w-0 flex-[0_0_calc(100%-52px)] rounded-xl bg-linear-to-b from-blue-600 to-blue-700 p-5 text-white last:mr-5 md:mx-0">
              <h3 className="text-lg font-black">Probelektion (45 min)</h3>
              <p className="mt-2 text-sm md:text-base">
                Unverbindliche Kennenlernfahrt, um Fahrlehrer, Fahrzeug und
                Unterrichtsstil kennenzulernen.
              </p>
              <p className="mt-8 text-xl font-black">59 CHF</p>
              <Button
                as={Link}
                href="#booking"
                version="white-gray"
                className="mt-3 w-full"
              >
                Probelektion buchen
              </Button>
            </div>
            <div className="ml-5 flex min-w-0 flex-[0_0_calc(100%-52px)] flex-col justify-between rounded-xl bg-gray-100 p-5 last:mr-5 md:mx-0">
              <div>
                <h3 className="text-lg font-black">Einzellektion (45 min)</h3>
                <p className="mt-2">
                  Individuelle Fahrlektion, angepasst an deinen aktuellen
                  Lernstand und deine Ziele (z. B. Prüfungsvorbereitung).
                </p>
              </div>
              <p className="text-lg font-black">
                {selectedLocation.prices.lessonB45 / 100} CHF
              </p>
            </div>
            <div className="ml-5 flex min-w-0 flex-[0_0_calc(100%-52px)] flex-col justify-between rounded-xl bg-gray-100 p-5 last:mr-5 md:mx-0">
              <div>
                <h3 className="text-lg font-black">Pakete</h3>
                <p className="mt-2">Spare bei Mehrfachbuchungen</p>
                <div className="mt-4 flex items-center gap-2">
                  <CheckIcon size={20} />
                  <span>5 Fahrlektionen à 45 min</span>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <CheckIcon size={20} />
                  <span>10 Fahrlektionen à 45 min</span>
                </div>
              </div>
              <p className="text-lg font-black">
                {selectedLocation.prices.fiverPackage / 100}/
                {selectedLocation.prices.tenPackage / 100} CHF
              </p>
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-center gap-x-1.5 md:hidden">
          {new Array(3).fill(null).map((_, index) => (
            <div
              key={index}
              className={clsx('rounded-full transition-all', {
                'size-2 bg-gray-200': selectedIndex !== index,
                'h-2 w-3 bg-gray-600': selectedIndex === index,
              })}
            />
          ))}
        </div>
        {!showMorePrices && (
          <button
            onClick={() => setShowMorePrices(true)}
            className="mx-5 mt-6 flex items-center gap-2 text-sm font-bold text-blue-700 md:mx-0"
          >
            <span>Weitere Kosten anzeigen</span>
            <CaretDownIcon size={20} weight="bold" />
          </button>
        )}
        {showMorePrices && (
          <>
            <p className="mx-5 mt-10 text-4xl font-black lg:mx-0">
              Weitere Preise
            </p>
            <div className="mx-5 max-w-202.5 divide-y divide-gray-200 md:mx-0">
              <div className="py-5">
                <div className="flex items-center justify-between">
                  <p className="text-lg font-black">
                    Admin- & Versicherungspauschale
                  </p>
                  <p className="text-lg font-black">90 CHF</p>
                </div>
                <p className="mt-2">
                  Ab der zweiten Fahrlektion verrechnen wir einmalig eine
                  Versicherungs- und Adminpauschale für die gesamte
                  Fahrausbildung. Sie deckt alle administrativen Kosten sowie
                  den Anteil an der Vollkaskoversicherung und schützt dich bei
                  Schäden am Fahrschulauto ohne Selbstbehalt.
                </p>
              </div>
              {vkuCourse && (
                <div className="py-5">
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-black">VKU Kurs</p>
                    <p className="text-lg font-black">
                      {vkuCourse.min / 100} CHF
                    </p>
                  </div>
                  <p className="mt-2">
                    Der Verkehrskundeunterricht (VKU) ist obligatorisch und ein
                    wichtiger Teil der Fahrausbildung. In kleinen Gruppen lernst
                    du, Verkehrssituationen richtig einzuschätzen, Gefahren
                    frühzeitig zu erkennen und sicher zu reagieren.
                  </p>
                </div>
              )}
              {examPriceRange && (
                <div className="py-5">
                  <div className="flex items-center justify-between">
                    <p className="text-lg font-black">Prüfung</p>
                    <p className="text-lg font-black">
                      {displayExamPriceRange(examPriceRange)} CHF
                    </p>
                  </div>
                  <p className="mt-2">
                    Für die praktische Prüfung wird eine Pauschale verrechnet.
                    Sie beinhaltet das Einfahren (ca. 45 Min.), die
                    Vorbereitung, die Prüfung selbst, das Abschlussgespräch
                    sowie die Rückfahrt. Die Gebühren des Strassenverkehrsamts
                    (STVA) sind nicht enthalten und werden separat in Rechnung
                    gestellt.
                  </p>
                </div>
              )}
            </div>
          </>
        )}
      </Container>
    </section>
  )
}

const displayExamPriceRange = ({ min, max }: { min: number; max: number }) => {
  if (min === max) {
    return `${min}`
  }
  return `${min}–${max}`
}

export default Prices
